import { responsiveContentfulImage } from '$providers/Assets';
import cn from 'classnames';
import { HTMLAttributes, useEffect, useRef, useState } from 'react';

import ReactPlayer from '../ReactPlayer/src/index';
import { IZeroElement, ZeroElementFadeDirection } from './';

const directionClass: Record<ZeroElementFadeDirection, string> = {
  up: `!bg-gradient-to-t`,
  right: `!bg-gradient-to-l`,
  down: `!bg-gradient-to-b`,
  left: `!bg-gradient-to-r`,
  none: ``,
};

export function ZeroStatic(props: IZeroElement & HTMLAttributes<HTMLElement>) {
  const {
    noDelay = false,
    placeholderImage = null,
    className,
    videoAsset,
    imageAsset,
    assetType,
    activeSlideIndex,
    canPlayVideo = null,
    fadeDirection = 'none',
    index,
    colour,
    customClass = 'absolute -ml-[36vh] md:-ml-[42.5vh] left-[50%] h-[72vh] md:h-[85vh] w-[72vh] md:w-[85vh]',
  } = props;

  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    if (!isVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisible(true);
            }
          });
        },
        {
          rootMargin: '0px 0px -10% 0px',
        },
      );
      observer.observe(domRef.current);
      return () => {
        observer.disconnect();
      };
    }
  });

  const [videoPlayerActive, setVideoPlayerActive] = useState(false);
  const refReactPlayer = useRef(null);
  const player = useRef(null);
  const initPlayer = () => {
    setVideoPlayerActive(true);
  };

  useEffect(() => {
    if (
      ((typeof activeSlideIndex !== 'undefined' &&
        (activeSlideIndex + 1 === index || activeSlideIndex === index)) ||
        canPlayVideo) &&
      videoPlayerActive &&
      isVisible
    ) {
      player.current = refReactPlayer.current.getInternalPlayer('hls');
      if (player.current) player.current.startLoad();
      else if (refReactPlayer.current)
        refReactPlayer.current.getInternalPlayer().play();
    }
  }, [index, activeSlideIndex, canPlayVideo, videoPlayerActive, isVisible]);

  const [lightImage, setLightImage] = useState(true);

  useEffect(() => {
    if (index === 0 || (index === 1 && !noDelay)) {
      const delayTimer = setTimeout(() => {
        setLightImage(false);
      }, 1500);

      return () => clearTimeout(delayTimer);
    } else {
      setLightImage(false);
    }
  }, [index, activeSlideIndex, noDelay]);

  return (
    <div ref={domRef} className={cn(customClass)}>
      <div
        style={{ backgroundColor: assetType === 'colour' ? colour : '' }}
        className={cn(className, 'zeroRing noblur relative')}
      >
        {assetType === 'image' && (
          <img
            alt={''}
            //sizes={'(max-width: 768px) 100vw, 40vw'}
            sizes={
              '(max-width: ' +
              Math.floor(768 * Math.min(2, window.devicePixelRatio)) +
              'px) ' +
              Math.floor(768 / Math.min(2, window.devicePixelRatio)) +
              'px, ' +
              Math.floor(1024 / Math.min(2, window.devicePixelRatio)) +
              'px'
            }
            srcSet={imageAsset}
            className="w-full h-full object-fit"
            loading="lazy"
          />
        )}
        {assetType === 'video' && (
          <>
            <ReactPlayer
              onReady={initPlayer}
              ref={refReactPlayer}
              url={videoAsset}
              className={cn('w-full h-full object-fit max-video')}
              autoPlay={
                canPlayVideo !== null
                  ? canPlayVideo
                  : activeSlideIndex === index && isVisible
              }
              width={'100%'}
              height={'100%'}
              playing={
                canPlayVideo !== null
                  ? canPlayVideo
                  : activeSlideIndex === index && isVisible
              }
              controls={false}
              loop={true}
              muted={true}
              playsinline={true}
              light={lightImage}
              config={{
                file: {
                  hlsOptions: {
                    startLevel: 4,
                    autoStartLoad: false,
                  },
                  attributes: {
                    preload: 'none',
                  },
                },
              }}
            />
            {!noDelay && placeholderImage !== null && (
              <div
                style={{
                  backgroundImage:
                    'url("' +
                    responsiveContentfulImage({
                      url: placeholderImage.url,
                      w: Math.floor(
                        1920 / Math.min(2, window.devicePixelRatio),
                      ),
                    }) +
                    '")',
                }}
                className={cn(
                  'absolute top-0 left-0 w-full pointer-events-none h-full bg-cover bg-center duration-[400ms] delay-[450ms]',
                  lightImage ? 'opacity-100' : 'opacity-0',
                )}
              ></div>
            )}
          </>
        )}
      </div>
      {fadeDirection !== 'none' && (
        <div
          className={cn(
            'absolute top-0 left-0 !bg-gradient-to-l from-transparent to-[#FFFFFF8F] w-full h-full',
            directionClass[fadeDirection],
          )}
        ></div>
      )}
    </div>
  );
}
